import { Injectable, OnDestroy } from '@angular/core';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, Subject, Subscription} from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { BroadcastService } from '../../services/broadcast.service';
import { MailCenterNotification } from '../models/notifications/mail-center-notification.model';
import { MailCenterApiService } from '../services/mail-center-api.service';
import { NotificationManagerService } from './notification-manager.service';

@Injectable()
export class MailCenterManagerService implements OnDestroy
{
    // Subjects
    notificationsListChange = new Subject<MailCenterNotification[]>();

    private _isMailCenterInitialized = false;
    private _messages: MailCenterNotification[] = [];
    private _numberOfUnreadMessages = 0;
    private _isLoadingMessages = false;
    private accountId: number;
    private locationId:number;
    mailCenterNotificationSubscription: Subscription;

    // =========================================================================================================================================================
    // C'tor and Destroy
    // =========================================================================================================================================================

    constructor(private mailCenterApiService: MailCenterApiService,
                private notificationManager: NotificationManagerService,
                private broadcastService: BroadcastService)
    {
        this.broadcastService.userSignedOut
            .pipe(untilDestroyed(this))
            .subscribe(() =>
            {
                this._messages = [];
                this._numberOfUnreadMessages = 0;
                this._isLoadingMessages = false;
                this._isMailCenterInitialized = false;
                this.clearMessages();
            });
    }

    ngOnDestroy()
    {
        /** Implemented to support untilDestroyed() */
        if(this.mailCenterNotificationSubscription){
            this.mailCenterNotificationSubscription.unsubscribe();
        }
    }

    // =========================================================================================================================================================
    // Public Methods
    // =========================================================================================================================================================

    init(accountId: number, locationId: number)
    {
        this._isLoadingMessages = true;
        this.accountId = accountId;
        this.locationId = locationId;

        this.mailCenterNotificationSubscription = this.notificationManager.mailCenterNotificationReceived
            .pipe(untilDestroyed(this))
            .subscribe((message: MailCenterNotification) =>
            {
                this.addMessages([message]);
            });

        this.getRecentMessageForLocation(this.accountId, this.locationId);

        this._isMailCenterInitialized = true;
    }

    getRecentMessageForLocation(accountId,locationId) {
        this.getRecentMessages(accountId, locationId)
            .pipe(take(1))
            .subscribe((messages: MailCenterNotification[]) => {
                this.messages = messages;
            });
    }

    refresh(locationId: number)
    {
        this.locationId = locationId;

        const lastMessageId = this._messages.length === 0 ? null : this._messages[0].Id;
        if(this.accountId){

            this.getRecentMessages(this.accountId, this.locationId, lastMessageId)
            .pipe(take(1))
            .subscribe((messages: MailCenterNotification[]) =>
            {
                this.addMessages(messages);
            });
        }
    }

    get isLoadingMessages(): boolean
    {
        return this._isLoadingMessages;
    }

    get isMailCenterInitialized(): boolean
    {
        return this._isMailCenterInitialized;
    }

    set isMailCenterInitialized(initialise)
    {
        this._isMailCenterInitialized = initialise;
    }

    markMessagesAsRead(messageIds: string[])
    {
        messageIds.forEach(messageId =>
        {
            const messageIndex = this._messages.findIndex(message => message.Id === messageId);
            if (this._messages[messageIndex].IsRead === false)
            {
                this._messages[messageIndex].IsRead = true;
                this._numberOfUnreadMessages -= 1;
            }
        });
    }

    deleteMessages(messageIds: string[])
    {
        messageIds.forEach(messageId =>
        {
            const messageIndex = this._messages.findIndex(message => message.Id === messageId);
            if (this._messages[messageIndex].IsRead === false)
            {
                this._numberOfUnreadMessages -= 1;
            }
            this._messages.splice(messageIndex, 1);
        });

        this.notificationsListChange.next(this._messages.slice());
    }

    get numberOfUnreadMessages(): number
    {
        return this._numberOfUnreadMessages;
    }

    markMessagesAsReadApiCall(accountId: number, messageIds: string[]): Observable<any>
    {
        return this.mailCenterApiService.markMessagesAsRead(accountId, messageIds);
    }

    deleteMessagesApiCall(accountId: number, messageIds: string[]): Observable<any>
    {
        return this.mailCenterApiService.deleteMessages(accountId, messageIds);
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private set messages(messages: MailCenterNotification[])
    {
        this.clearMessages();
        this.addMessages(messages);
    }

    private addMessages(messages: MailCenterNotification[])
    {
        for (let i = 0; i < messages.length; i++)
        {
            this._messages.unshift(messages[i]);
        }
        this.setNumberOfUnreadMessages();

        this.notificationsListChange.next(this._messages.slice());
    }

    private setNumberOfUnreadMessages()
    {
        let numberOfUnreadMessages = 0;
        this._messages.forEach(message =>
        {
            if (!message.IsRead)
            {
                numberOfUnreadMessages += 1;
            }
        });
        this._numberOfUnreadMessages = numberOfUnreadMessages;
    }

    clearMessages()
    {
        this._messages = [];
        this._numberOfUnreadMessages = 0;
        this.notificationsListChange.next(this._messages.slice());
    }

    private getRecentMessages(accountId: number, locationId: number, lastMessageId: string = null): Observable<MailCenterNotification[]>
    {
        this._isLoadingMessages = true;
        return this.mailCenterApiService.getRecentMessages(accountId, locationId, lastMessageId)
            .pipe(tap(() => this._isLoadingMessages = false));
    }
}
